import { FC, JSX } from "react";
import cx from "classnames";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { getCareJourneyDefinitions, getMotiveScoreDefinitions } from "@app/projects/servicing-provider/ServicingProviderHelpers";

// The Motive and Arcadia CareJourney score columns share a branded header: an info tooltip, a label, and a vendor
// logo. This component is the single source of truth for that markup so the provider-hub profile tables (hospital,
// organization, contract entity, facilities, employed practitioners) and the provider-hub views (hospital,
// organization, contract entity, providers) stay consistent in terminology, alt text, and accessibility.
export type ProviderScoreColumnHeaderVariant = "motive" | "careJourneyCost" | "careJourneyQuality";

interface ProviderScoreColumnHeaderConfig {
  label: string;
  getTooltip: () => JSX.Element | string;
  image: string;
  imageAlt: string;
  iconDescription: string;
}

const HEADER_CONFIG: Record<ProviderScoreColumnHeaderVariant, ProviderScoreColumnHeaderConfig> = {
  careJourneyCost: {
    getTooltip: getCareJourneyDefinitions,
    iconDescription: "CareJourney cost information",
    image: "carejourney-logo.svg",
    imageAlt: "Arcadia CareJourney",
    label: "CareJourney Cost"
  },
  careJourneyQuality: {
    getTooltip: getCareJourneyDefinitions,
    iconDescription: "CareJourney quality information",
    image: "carejourney-logo.svg",
    imageAlt: "Arcadia CareJourney",
    label: "CareJourney Quality"
  },
  motive: {
    getTooltip: getMotiveScoreDefinitions,
    iconDescription: "Motive appropriateness information",
    image: "motive-h-color.svg",
    imageAlt: "Motive Appropriateness",
    label: "Motive Appropriateness"
  }
};

interface ProviderScoreColumnHeaderProps {
  variant: ProviderScoreColumnHeaderVariant;
  // Optional label override — used by the views for the "- Unfiltered" column variants. Defaults to the variant's label.
  label?: string;
}

export const ProviderScoreColumnHeader: FC<ProviderScoreColumnHeaderProps> = ({ variant, label }) => {
  const config = HEADER_CONFIG[variant];

  return (
    <div className="qes-score-column-header">
      <TooltipWrapper text={config.getTooltip()} position="bottom">
        <div className={cx("qes-score-column-header__indicator", "waffles-tooltip-indicator")}>
          <QesIcon iconName={QesIcons.InfoCircle} description={config.iconDescription} width="14" />
        </div>
      </TooltipWrapper>
      <span className="qes-score-column-header__label">{label ?? config.label}</span>
      <img className="qes-score-column-header__logo" src={`${window.customUIBaseUrl}/images/${config.image}`} alt={config.imageAlt} width="120" height="30" />
    </div>
  );
};
